import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Search, UserCircle, Pencil, SlidersHorizontal } from "lucide-react";
import { YEAR_LABEL } from "@/lib/school";
import { LeaveAdjustDialog } from "@/components/permessi/LeaveAdjustDialog";

export default function Permessi() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // student_id
  const [val, setVal] = useState("");
  const [adjusting, setAdjusting] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["leave-quotas"],
    queryFn: async () => (await api.get("/leave-quotas")).data,
  });

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) =>
      (r.student_name || "").toLowerCase().includes(n) ||
      (r.student_matricola || "").toLowerCase().includes(n) ||
      (r.student_email || "").toLowerCase().includes(n)
    );
  }, [rows, q]);

  const totals = useMemo(() => rows.reduce((acc, r) => {
    acc.total += r.total_hours || 0;
    acc.used += r.used_hours || 0;
    acc.adjusted += r.adjusted_hours || 0;
    acc.available += r.available_hours || 0;
    acc.med += r.medical_approved || 0;
    acc.medH += r.medical_hours || 0;
    acc.medP += r.medical_pending || 0;
    return acc;
  }, { total: 0, used: 0, adjusted: 0, available: 0, med: 0, medH: 0, medP: 0 }), [rows]);
  const showMed = rows.some((r) => r.medical_approved !== undefined);

  const saveMut = useMutation({
    mutationFn: async ({ student_id, total_hours }) => (await api.put(`/leave-quotas/${student_id}`, { total_hours })).data,
    onSuccess: () => {
      toast.success("Monte ore aggiornato");
      setEditing(null); setVal("");
      qc.invalidateQueries({ queryKey: ["leave-quotas"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="permessi-page">
      <PageHeader
        title="Permessi — Monte ore studenti"
        subtitle="Imposta il monte ore permessi per ogni studente. Il saldo si aggiorna automaticamente quando approvi/annulli una richiesta di permesso."
      />

      <div className={`grid grid-cols-2 gap-3 mb-5 ${showMed ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
        <Kpi label="Monte ore totale" value={`${totals.total}h`}/>
        <Kpi label="Ore usate (approvate)" value={`${totals.used.toFixed(1)}h`} tone="amber"/>
        <Kpi label="Rettifiche nette" value={`${totals.adjusted.toFixed(1)}h`} tone="amber"/>
        <Kpi label="Ore residue" value={`${totals.available.toFixed(1)}h`} tone="emerald"/>
        {showMed && <Kpi label="Visite mediche approvate" value={totals.med} hint={`${totals.medH.toFixed(1)}h non scalate${totals.medP ? ` · ${totals.medP} in attesa` : ""}`} tone="sky" testId="permessi-kpi-medical"/>}
      </div>

      <div className="mb-4 max-w-md relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
        <Input
          data-testid="permessi-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca studente, matricola, email…"
          className="pl-9"
        />
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-stone-400 text-sm">Nessuno studente trovato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Studente</th>
                  <th className="text-left px-4 py-2.5">Anno</th>
                  <th className="text-right px-4 py-2.5">Monte ore</th>
                  <th className="text-right px-4 py-2.5">Usate</th>
                  <th className="text-right px-4 py-2.5">Rettifiche</th>
                  <th className="text-right px-4 py-2.5">Residue</th>
                  {showMed && <th className="text-right px-4 py-2.5">Visite mediche</th>}
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r) => {
                  const isEditing = editing === r.student_id;
                  const pctUsed = r.total_hours > 0 ? Math.min(100, ((r.total_hours - r.available_hours) / r.total_hours) * 100) : 0;
                  const barColor = pctUsed >= 90 ? "#dc2626" : pctUsed >= 70 ? "#f59e0b" : "#1C352D";
                  return (
                    <tr key={r.student_id} className="hover:bg-stone-50/60" data-testid={`permesso-row-${r.student_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-stone-100 grid place-items-center text-stone-500 shrink-0"><UserCircle size={15}/></div>
                          <div className="min-w-0">
                            <div className="font-medium text-[#1C352D] truncate">{r.student_name}</div>
                            <div className="text-[11px] text-stone-500 truncate">{r.student_matricola} · {r.student_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[12px]">
                        {r.student_year_level ? YEAR_LABEL[r.student_year_level] : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <Input
                              type="number" min="0" step="0.5" value={val}
                              onChange={(e) => setVal(e.target.value)}
                              className="w-24 text-right h-8"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveMut.mutate({ student_id: r.student_id, total_hours: parseFloat(val || "0") }); }}
                              data-testid={`permesso-input-${r.student_id}`}
                            />
                            <Button size="sm" className="h-8 bg-[#1C352D]" disabled={saveMut.isPending} onClick={() => saveMut.mutate({ student_id: r.student_id, total_hours: parseFloat(val || "0") })} data-testid={`permesso-save-${r.student_id}`}>
                              <Save size={13}/>
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditing(r.student_id); setVal(String(r.total_hours || 0)); }}
                            className="inline-flex items-center gap-1 text-[#1C352D] hover:underline"
                            data-testid={`permesso-edit-${r.student_id}`}
                          >
                            <b>{r.total_hours || 0}h</b> <Pencil size={11} className="text-stone-400"/>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-700">{r.used_hours || 0}h</td>
                      <td className="px-4 py-3 text-right text-stone-700" data-testid={`permesso-adjusted-${r.student_id}`}>
                        {r.adjusted_hours ? `${r.adjusted_hours > 0 ? "−" : "+"}${Math.abs(r.adjusted_hours)}h` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex flex-col items-end gap-1">
                          <b style={{ color: barColor }}>{r.available_hours || 0}h</b>
                          {r.total_hours > 0 && (
                            <div className="w-20 h-1 bg-stone-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pctUsed}%`, background: barColor }}/>
                            </div>
                          )}
                        </div>
                      </td>
                      {showMed && (
                        <td className="px-4 py-3 text-right" data-testid={`permesso-medical-${r.student_id}`}>
                          {r.medical_approved ? <><b className="text-sky-800">{r.medical_approved}</b> <span className="text-[11px] text-stone-500">· {r.medical_hours}h</span></> : <span className="text-stone-400">—</span>}
                          {r.medical_pending > 0 && <div className="text-[10px] text-amber-700">{r.medical_pending} in attesa</div>}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setAdjusting(r)} data-testid={`permesso-adjust-${r.student_id}`}>
                          <SlidersHorizontal size={12} className="mr-1"/> Rettifica
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <LeaveAdjustDialog row={adjusting ? rows.find((x) => x.student_id === adjusting.student_id) || adjusting : null} onClose={() => setAdjusting(null)}/>
    </div>
  );
}

function Kpi({ label, value, tone, hint, testId }) {
  const cls = tone === "amber" ? "text-amber-700" : tone === "emerald" ? "text-emerald-700" : tone === "sky" ? "text-sky-800" : "text-[#1C352D]";
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${cls}`}>{value}</div>
      {hint && <div className="text-[11px] text-stone-500 mt-0.5">{hint}</div>}
    </Card>
  );
}
